import { payloadToPartnerReservation, toDate } from "@/data/mappers/partnerReservation.mapper";
import type { OccupiedPlaces, Today, TodayOffer } from "@/domain/today/types";

export interface TodayReservationPayload {
  id: number;
  offerId: number;
  activityName: string | null;
  customerName: string | null;
  startTime: string | null;
  amount?: number | null;
  confirmDeadline?: string | null;
  code?: string | null;
  validatedAt?: string | null;
  customAnswers?: unknown;
}

/** `paid` and `awaitingConfirmation` are what the current API sends. `reserved`
 *  is all an API that predates the split has: one count over every held status.
 *  The portal keeps reading it because a portal deploy can land before the API's,
 *  and then it is the only occupancy there is — an older API also sent only the
 *  termíny with places left, so a sold-out one is simply absent from the day. */
export interface TodayOfferPayload {
  offerId: number;
  activityName: string | null;
  startTime: string;
  endTime: string;
  saleEndTime: string | null;
  capacity: number;
  paid?: number;
  awaitingConfirmation?: number;
  reserved?: number;
}

/** Either key is a whole answer; neither is ever both. */
export interface TodayPayload {
  awaitingAnswer: TodayReservationPayload[];
  arrivingToday: TodayReservationPayload[];
  todaysOffers?: TodayOfferPayload[];
  unsoldToday?: TodayOfferPayload[];
}

// The endpoint queries each array by status, so which array a row arrived in is
// the state — it is not sent a second time on the row itself.
function toPartnerReservation(payload: TodayReservationPayload, state: string) {
  return payloadToPartnerReservation({
    id: payload.id,
    offerId: payload.offerId,
    state,
    createdAt: null,
    startTime: payload.startTime,
    amount: payload.amount ?? null,
    code: payload.code ?? null,
    confirmDeadline: payload.confirmDeadline ?? null,
    validatedAt: payload.validatedAt ?? null,
    activityName: payload.activityName,
    customerName: payload.customerName,
    customAnswers: payload.customAnswers,
  });
}

// A row that carries one of the two new counts and not the other says nothing
// about the places the missing one holds, so only `reserved` can total it up.
function toOccupiedPlaces(payload: TodayOfferPayload): OccupiedPlaces {
  if (payload.paid != null && payload.awaitingConfirmation != null) {
    return {
      kind: "split",
      paid: payload.paid,
      awaitingConfirmation: payload.awaitingConfirmation,
    };
  }
  return {
    kind: "unknown",
    occupied: payload.reserved ?? payload.paid ?? payload.awaitingConfirmation ?? 0,
  };
}

function toTodayOffer(payload: TodayOfferPayload): TodayOffer {
  return {
    offerId: payload.offerId,
    activityName: payload.activityName ?? "Neznámá šablona slev",
    startTime: new Date(payload.startTime),
    endTime: new Date(payload.endTime),
    saleEndTime: toDate(payload.saleEndTime),
    capacity: payload.capacity,
    occupiedPlaces: toOccupiedPlaces(payload),
  };
}

export function toToday(payload: TodayPayload): Today {
  return {
    awaitingAnswer: payload.awaitingAnswer.map((row) =>
      toPartnerReservation(row, "pending_confirmation"),
    ),
    arrivingToday: payload.arrivingToday.map((row) => toPartnerReservation(row, "upcoming")),
    todaysOffers: (payload.todaysOffers ?? payload.unsoldToday ?? []).map(toTodayOffer),
  };
}
